import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { CampoDeFecha } from '../../../nucleo/fecha/CampoDeFecha.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { consultarCuentas, PROPOSITOS } from '../../cuentas/api/cuentasApi.js';
import { ComboboxDeCuenta } from '../../movimientos/componentes/ComboboxDeCuenta.jsx';
import { CalendarioDelPrestamo } from '../componentes/CalendarioDelPrestamo.jsx';
import {
  MODOS_DEL_ADELANTO,
  obtenerPrestamo,
  registrarPagoAdelantado,
  simularPagoAdelantado,
} from '../api/prestamosApi.js';
import { importe as formatearImporte } from '../formato.js';

/**
 * ABONAR A CAPITAL · pago adelantado de un préstamo
 * (prototipo v48 `-prestamo-adelanto` y `-adelanto-recalculo`; RF-276, RF-277,
 * RN-784 a RN-795, RNF-133, D-463 a D-468, D-472).
 *
 * LO QUE ESTA PANTALLA HACE, EN UNA FRASE: deja registrar un abono que va TODO a
 * capital y, antes de guardarlo, enseñar exactamente cómo queda el calendario.
 *
 * LAS CUATRO REGLAS QUE NO SE PUEDEN ROMPER AQUÍ:
 *
 *   · AQUÍ NO SE CALCULA NADA (RNF-038/RNF-138). La cifra propuesta, el resto, el
 *     calendario resultante, el resumen y hasta los textos vienen del servidor
 *     (§9). Si aparece en este archivo una división de importes o una suma de
 *     meses, es defecto de construcción.
 *
 *   · LA CIFRA LLEGA YA ESCRITA Y ES DE ELLA (D-463, RNF-133): el campo nunca se
 *     deshabilita, nunca llega vacío, conserva su valor y queda seleccionado
 *     completo al tomar el foco. Lo que se guarda es LO CONFIRMADO, no la
 *     propuesta (CA-21-05).
 *
 *   · AL CORREGIR LA CIFRA, LA VISTA PREVIA SE REHACE (D-472/RN-795). Mientras se
 *     rehace, el calendario anterior SE RETIRA —no se atenúa—, porque ya no
 *     corresponde a lo escrito; si el recálculo falla, tampoco se enseña, y se
 *     ofrece volver a intentarlo. Jamás se presenta como vigente un calendario que
 *     no corresponde a lo que se va a guardar.
 *
 *   · SIN CONFIRMAR NO SE GUARDA (RN-784f). El registro reescribe el calendario y
 *     no se des-aplica: la confirmación es un paso explícito dentro de esta misma
 *     pantalla, con el efecto enumerado.
 */
export default function PagoAdelantadoDePrestamo() {
  const { idCuenta } = useParams();
  const navegar = useNavigate();
  const hoy = obtenerFechaDeNegocioHoy();

  const [prestamo, setPrestamo] = useState(null);
  const [cuentasOrigen, setCuentasOrigen] = useState([]);
  const [cargandoCuentas, setCargandoCuentas] = useState(true);

  const [idCuentaOrigen, setIdCuentaOrigen] = useState(null);
  const [fecha, setFecha] = useState(hoy);
  const [importeAbono, setImporteAbono] = useState('');
  const [claveModo, setClaveModo] = useState(MODOS_DEL_ADELANTO.reducirPlazo);
  const [nota, setNota] = useState('');

  /*
   * LA CIFRA CONFIRMADA, por modo y como TEXTO.
   *
   * Son dos estados y no uno porque son dos cifras distintas —pagos y pesos— y
   * compartir uno obligaría a reinterpretarlo al cambiar de modo, que es
   * exactamente cómo se pierde lo que la persona escribió. `null` significa
   * «todavía no llegó la propuesta»; en cuanto llega, el campo nace escrito.
   */
  const [plazoNuevo, setPlazoNuevo] = useState(null);
  const [mensualidadNueva, setMensualidadNueva] = useState(null);

  const [simulacion, setSimulacion] = useState(null);
  const [simulando, setSimulando] = useState(false);
  const [errorDeSimulacion, setErrorDeSimulacion] = useState(null);
  const [error, setError] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  /*
   * Para descartar respuestas de simulaciones VIEJAS. Sin esto, corregir dos veces
   * seguidas puede dejar en pantalla el calendario de la PRIMERA corrección si su
   * respuesta llega tarde: un calendario que no corresponde a lo escrito, que es
   * justo lo que RN-795e prohíbe.
   */
  const ultimaPeticion = useRef(0);

  const codigoISO = prestamo?.cuenta?.codigoISO ?? null;
  const esModoPlazo = claveModo === MODOS_DEL_ADELANTO.reducirPlazo;

  useEffect(() => {
    (async () => {
      try {
        const [elPrestamo, respuestaDeCuentas] = await Promise.all([
          obtenerPrestamo({ idCuenta: Number(idCuenta), fechaLocal: hoy }),
          consultarCuentas({ proposito: PROPOSITOS.capturarOrigen }),
        ]);

        setPrestamo(elPrestamo);

        // S-21-03 · LA CUENTA DE ORIGEN ES DE LA MISMA MONEDA: no hay conversión y
        // no la habrá. Se filtra para no ofrecer lo que el servidor rechazaría, y
        // el préstamo mismo se excluye: nadie se abona a sí mismo.
        setCuentasOrigen((respuestaDeCuentas.cuentas ?? []).filter(
          (c) => c.codigoISO === elPrestamo.cuenta.codigoISO && c.idCuenta !== Number(idCuenta),
        ));
      } catch (fallo) {
        setError(fallo);
      } finally {
        setCargandoCuentas(false);
      }
    })();
  }, [idCuenta, hoy]);

  /**
   * PIDE LA VISTA PREVIA AL SERVIDOR (§9). Es la única fuente de la propuesta, del
   * calendario y de los textos: aquí no se deriva ni una cifra.
   *
   * `cifra` viaja solo cuando la persona ya corrigió: sin ella el servidor propone,
   * con ella deriva de lo escrito (RN-795a). Y el rechazo por rango llega con el
   * MISMO 422 del guardado, así que se ve ANTES de escribir nada (RN-795d) sin que
   * esta pantalla tenga que conocer la cota.
   */
  const pedirVistaPrevia = useCallback(async ({ cifraDePlazo, cifraDeMensualidad } = {}) => {
    if (!importeAbono || Number(importeAbono) <= 0) {
      setSimulacion(null);
      setErrorDeSimulacion(null);
      return;
    }

    const miPeticion = ultimaPeticion.current + 1;
    ultimaPeticion.current = miPeticion;

    setSimulando(true);
    setErrorDeSimulacion(null);
    /*
     * RN-795a/b · EL CALENDARIO ANTERIOR SE RETIRA, NO SE ATENÚA. Mientras se
     * rehace no se enseña nada: dejarlo puesto —aunque fuera en gris— sería
     * presentar como vigente un calendario que ya no corresponde a la cifra
     * escrita, y de eso depende que confirmar no sea confirmar a ciegas.
     */
    setSimulacion(null);

    try {
      const resultado = await simularPagoAdelantado({
        idCuenta: Number(idCuenta),
        idCuentaOrigen,
        fecha,
        importeAbono: String(importeAbono),
        claveModo,
        pagosPendientesResultantes: cifraDePlazo ?? null,
        importeMensualidadNueva: cifraDeMensualidad ?? null,
        fechaLocal: hoy,
      });

      // Llegó tarde: ya hay una corrección más nueva en curso. Se descarta.
      if (ultimaPeticion.current !== miPeticion) return;

      setSimulacion(resultado);

      // D-463 · LA PROPUESTA NACE ESCRITA EN EL CAMPO, y solo cuando la persona
      // todavía no ha puesto cifra propia: si ya corrigió, su cifra manda y NO se
      // le pisa lo que escribió (CA-21-21).
      if (cifraDePlazo == null && plazoNuevo === null && resultado.plazoNuevoPropuesto != null) {
        setPlazoNuevo(String(resultado.plazoNuevoPropuesto));
      }
      if (cifraDeMensualidad == null && mensualidadNueva === null && resultado.mensualidadNuevaPropuesta != null) {
        setMensualidadNueva(resultado.mensualidadNuevaPropuesta);
      }
    } catch (fallo) {
      if (ultimaPeticion.current !== miPeticion) return;
      /*
       * RN-795d · el rechazo por rango y RN-795e · el fallo de recálculo entran los
       * dos por aquí, y los dos terminan igual de cara a la vista previa: NO se
       * enseña calendario. La diferencia la pone el texto, que es del servidor
       * cuando es un rechazo (422 con su literal) y genérico cuando no se pudo
       * preguntar. Nada se ha escrito en ninguno de los dos casos.
       */
      setErrorDeSimulacion(fallo);
      setSimulacion(null);
    } finally {
      if (ultimaPeticion.current === miPeticion) setSimulando(false);
    }
  }, [idCuenta, idCuentaOrigen, fecha, importeAbono, claveModo, hoy, plazoNuevo, mensualidadNueva]);

  /*
   * La PRIMERA vista previa —y la de cada cambio de abono o de modo— se pide sola:
   * la cifra tiene que llegar YA ESCRITA (D-463), así que no puede depender de que
   * la persona toque nada. Al cambiar de modo se olvida la cifra del modo nuevo
   * para que vuelva a nacer de su propuesta; la del modo anterior se conserva en su
   * propio estado por si vuelve.
   */
  useEffect(() => {
    pedirVistaPrevia({
      cifraDePlazo: esModoPlazo && plazoNuevo !== null ? Number(plazoNuevo) : null,
      cifraDeMensualidad: !esModoPlazo && mensualidadNueva !== null ? String(mensualidadNueva) : null,
    });
    // Se re-pide SOLO cuando cambia lo que define la propuesta. La cifra corregida
    // NO entra en esta lista: su re-simulación la dispara el propio campo al salir
    // del foco (RN-795a), no cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importeAbono, claveModo, fecha, idCuentaOrigen]);

  /** RN-795a · al salir del campo de la cifra, la vista previa se rehace con ella. */
  function reSimularConLaCifraEscrita() {
    pedirVistaPrevia({
      cifraDePlazo: esModoPlazo && plazoNuevo !== '' && plazoNuevo != null ? Number(plazoNuevo) : null,
      cifraDeMensualidad: !esModoPlazo && mensualidadNueva ? String(mensualidadNueva) : null,
    });
  }

  const esLiquidacion = simulacion?.esLiquidacion === true;
  const hayCifraEscrita = esLiquidacion
    || (esModoPlazo ? plazoNuevo !== null && plazoNuevo !== '' : Boolean(mensualidadNueva));

  /*
   * Se puede intentar guardar cuando hay con qué: el bloqueo de verdad —rangos,
   * deuda, moneda, fecha— lo aplica el servidor, que además rechaza sin escribir.
   *
   * [CORREGIDO · RONDA[1/2] del Tester · MENOR] `errorDeSimulacion` entra en la
   * cuenta. Faltaba, y el resultado era incoherente: con una cifra fuera de rango
   * ya rechazada —el 422 en rojo a la vista, sin calendario— el botón «Registrar
   * el abono» seguía encendido, invitando a un guardado que el servidor iba a
   * rechazar con el MISMO mensaje. Ofrecer una puerta que se cierra en la cara es
   * exactamente lo que RN-795d evita al enseñar el rechazo ANTES de guardar.
   *
   * Cubre los dos casos de RN-795d/e: la cifra inválida y el recálculo caído. En
   * el segundo el botón también se apaga, y es lo correcto: sin vista previa no se
   * confirma a ciegas, y ahí está «Volver a intentarlo».
   */
  const puedeGuardar = Boolean(idCuentaOrigen)
    && Boolean(fecha)
    && Boolean(importeAbono)
    && Number(importeAbono) > 0
    && hayCifraEscrita
    && !simulando
    && !errorDeSimulacion;

  async function guardar() {
    setError(null);
    setGuardando(true);
    try {
      await guardarConConexion(() => registrarPagoAdelantado({
        idCuenta: Number(idCuenta),
        idCuentaOrigen,
        fecha,
        importeAbono: String(importeAbono),
        claveModo,
        // LO CONFIRMADO, nunca la propuesta (CA-21-05). En la liquidación no hay
        // cifra que mandar: no queda calendario (D-468).
        pagosPendientesResultantes: !esLiquidacion && esModoPlazo ? Number(plazoNuevo) : null,
        importeMensualidadNueva: !esLiquidacion && !esModoPlazo ? String(mensualidadNueva) : null,
        confirmado: true,
        nota: nota.trim() || null,
        fechaLocal: hoy,
      }));

      navegar(`/cuentas/prestamos/${idCuenta}?acuse=adelanto`, { replace: true });
    } catch (fallo) {
      setError(fallo);
      setConfirmando(false);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell titulo="Abonar a capital" volverA={`/cuentas/prestamos/${idCuenta}`}>
      <CintaDeVersionNueva />

      {/* RN-792e · el texto lo redacta el SERVIDOR (RNF-130). Mientras no llega, la
          pantalla no inventa una versión propia que luego diga otra cosa. */}
      <div className="aviso i" role="status" data-prueba="adelanto-introduccion">
        <b className="tit">Este abono va todo a capital</b>
        {simulacion?.textoDeIntroduccion
          ?? 'Baja lo que debes. Tu interés de cada mes lo sigues registrando al pagar, como siempre.'}
      </div>

      <AvisoDeError error={error} />

      {prestamo && (
        <div className="card" style={{ margin: 12 }} data-prueba="adelanto-encabezado">
          <div className="fila">
            <span className="crece">{prestamo.cuenta.nombre}</span>
            {prestamo.contadorDeAvance && <b data-prueba="adelanto-contador">{prestamo.contadorDeAvance}</b>}
          </div>
          <div className="fila">
            <span className="crece">Debes hoy</span>
            <b data-prueba="adelanto-deuda">{formatearImporte(prestamo.deudaVigente, codigoISO)}</b>
          </div>
          {prestamo.pagosQueFaltan != null && (
            <div className="fila">
              <span className="crece">Pagos que te faltan</span>
              <b data-prueba="adelanto-pendientes">{prestamo.pagosQueFaltan}</b>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ margin: 12 }} data-prueba="adelanto-formulario">
        <div className="campo">
          <ComboboxDeCuenta
            id="idCuentaOrigen"
            etiqueta="¿DE QUÉ CUENTA SALE EL ABONO? — siempre una cuenta TUYA y activa"
            cuentas={cuentasOrigen}
            cargando={cargandoCuentas}
            valor={idCuentaOrigen}
            alElegir={(elegida) => setIdCuentaOrigen(elegida ?? null)}
            textoDePeticion="Elige la cuenta de la que sale el abono…"
            obligatorio
            ayuda={`Solo tus cuentas activas en ${codigoISO ?? 'la misma moneda'}: un abono no cambia de moneda.`}
            datoDePrueba="adelanto-campo-cuenta-origen"
          />
        </div>

        <div className="campo">
          <label htmlFor="fecha">FECHA DEL ABONO</label>
          <CampoDeFecha
            id="fecha"
            name="fecha"
            valor={fecha}
            alCambiar={setFecha}
            max={hoy}
            datoDePrueba="adelanto-campo-fecha"
          />
          <span className="ayuda">No puede ser posterior a hoy.</span>
        </div>

        <div className="campo">
          <label htmlFor="importeAbono">CUÁNTO ABONAS</label>
          {/* RNF-133 · el campo ÚNICO del núcleo, sin propiedades nuevas. */}
          <CampoDeImporte
            id="importeAbono"
            valor={importeAbono}
            alCambiar={setImporteAbono}
            codigoDivisa={codigoISO}
            contexto="el abono a capital"
            data-prueba="adelanto-campo-abono"
          />
          <span className="ayuda">
            {prestamo
              ? `Hoy debes ${formatearImporte(prestamo.deudaVigente, codigoISO)}. Todo lo que abones aquí baja esa cifra.`
              : 'Todo lo que abones aquí baja lo que debes.'}
          </span>
        </div>

        {/* D-468 · con el abono igual a la deuda no queda calendario: el modo no
            cambia nada y el bloque de la cifra desaparece. */}
        {esLiquidacion ? (
          <div className="aviso ok" role="status" data-prueba="adelanto-liquidacion">
            <b className="tit">Este abono liquida tu préstamo</b>
            {simulacion.textoDeLiquidacion}
          </div>
        ) : (
          <>
            <div className="campo" data-prueba="adelanto-modo">
              <span className="etiqueta">¿QUÉ HIZO TU BANCO?</span>
              <span className="ayuda">Elige una. Es lo que dice tu estado de cuenta después del abono.</span>

              <label className="opcion">
                <input
                  type="radio"
                  name="claveModo"
                  value={MODOS_DEL_ADELANTO.reducirPlazo}
                  checked={esModoPlazo}
                  onChange={() => setClaveModo(MODOS_DEL_ADELANTO.reducirPlazo)}
                  data-prueba="adelanto-modo-plazo"
                />
                <span>
                  <b>Menos pagos con la misma mensualidad</b>
                  <br />
                  Sigues pagando lo mismo cada mes, pero te faltan menos pagos y terminas antes.
                </span>
              </label>

              <label className="opcion">
                <input
                  type="radio"
                  name="claveModo"
                  value={MODOS_DEL_ADELANTO.reducirMensualidad}
                  checked={!esModoPlazo}
                  onChange={() => setClaveModo(MODOS_DEL_ADELANTO.reducirMensualidad)}
                  data-prueba="adelanto-modo-mensualidad"
                />
                <span>
                  <b>Los mismos pagos con mensualidad menor</b>
                  <br />
                  Terminas el mismo mes que antes, pero cada pago es más chico.
                </span>
              </label>
            </div>

            {/* ===== LA CIFRA NUEVA, YA ESCRITA Y EDITABLE (D-463, RNF-133) ==== */}
            {esModoPlazo ? (
              <div className="campo">
                <label htmlFor="plazoNuevo">PAGOS QUE TE QUEDAN</label>
                <input
                  id="plazoNuevo"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={plazoNuevo ?? ''}
                  onChange={(e) => setPlazoNuevo(e.target.value)}
                  /* RNF-133 · seleccionado COMPLETO al tomar el foco: teclear 3 deja
                     3, no 313. Nunca deshabilitado, nunca vacío. */
                  onFocus={(e) => e.target.select()}
                  onBlur={reSimularConLaCifraEscrita}
                  data-prueba="adelanto-campo-plazo"
                />
                <span className="ayuda">
                  {simulacion?.plazoNuevoPropuesto != null
                    ? `De los ${simulacion.pendientesDeHoy} que te faltaban. Corrígelo si tu banco dejó otro número.`
                    : 'Corrígelo si tu banco dejó otro número.'}
                </span>
              </div>
            ) : (
              <div className="campo">
                <label htmlFor="mensualidadNueva">TU MENSUALIDAD NUEVA</label>
                {/* RN-795a · la re-simulación se dispara AL SALIR DEL CAMPO, y el
                    disparador vive en este envoltorio A PROPÓSITO: `CampoDeImporte`
                    es del núcleo compartido y NO gana ni una propiedad por esta
                    pantalla (RNF-133, contratos §0 bis «el núcleo no se toca»). El
                    foco que sale del campo burbujea hasta aquí y eso basta. */}
                <div onBlur={reSimularConLaCifraEscrita}>
                  <CampoDeImporte
                    id="mensualidadNueva"
                    valor={mensualidadNueva ?? ''}
                    alCambiar={setMensualidadNueva}
                    codigoDivisa={codigoISO}
                    contexto="tu mensualidad nueva"
                    data-prueba="adelanto-campo-mensualidad"
                  />
                </div>
                <span className="ayuda">
                  {simulacion?.pagoMensualDeHoy
                    ? `Antes era ${formatearImporte(simulacion.pagoMensualDeHoy, codigoISO)}. Corrígela si tu banco dejó otra cifra.`
                    : 'Corrígela si tu banco dejó otra cifra.'}
                </span>
              </div>
            )}

            {/* RN-785d · el RESTO se declara; no se inventa un pago partido. */}
            {simulacion?.textoDelResto && (
              <div className="aviso w" role="status" data-prueba="adelanto-resto">
                {simulacion.textoDelResto}
              </div>
            )}

            {/* RN-795c · la propuesta no se pierde, pero no gobierna la vista previa. */}
            {simulacion?.seUsoCifraCorregida && (
              <div className="aviso i" role="status" data-prueba="adelanto-cifra-corregida">
                Corregiste la cifra propuesta. La aplicación proponía
                {' '}
                <b>
                  {esModoPlazo
                    ? simulacion.plazoNuevoPropuesto
                    : formatearImporte(simulacion.mensualidadNuevaPropuesta, codigoISO)}
                </b>
                {' '}y vas a guardar{' '}
                <b>
                  {esModoPlazo
                    ? simulacion.plazoNuevoUsado
                    : formatearImporte(simulacion.mensualidadNuevaUsada, codigoISO)}
                </b>
                : manda la tuya. El calendario que ves se rehace con tu cifra.
              </div>
            )}
          </>
        )}

        <div className="campo">
          <label htmlFor="nota">NOTA (OPCIONAL)</label>
          <textarea
            id="nota"
            rows={3}
            maxLength={500}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            data-prueba="adelanto-campo-nota"
          />
        </div>
      </div>

      {/* D-467 / RN-788 · LA CONFUSIÓN MÁS CARA, dicha antes de guardar: abonar
          justo una mensualidad NO paga el mes. El texto es del servidor. */}
      {simulacion?.textoDeQueNoCubreElMes && (
        <div className="aviso w" role="status" data-prueba="adelanto-no-es-el-pago-del-mes">
          <b className="tit">Este abono no es tu pago del mes</b>
          {simulacion.textoDeQueNoCubreElMes}
        </div>
      )}

      {/* ===== LA VISTA PREVIA ============================================== */}
      <div className="card" style={{ margin: 12 }} data-prueba="adelanto-vista-previa">
        <h3>Cómo queda tu plan</h3>

        {simulando && (
          <div className="aviso i" role="status" data-prueba="adelanto-recalculando">
            <b className="tit">Volviendo a calcular con tu cifra…</b>
            Estamos rehaciendo el calendario y el resumen. Mientras tanto no te enseñamos el
            calendario anterior, porque ya no corresponde a lo que escribiste.
          </div>
        )}

        {/* RN-795d/e · rechazo por rango y fallo de recálculo: en los dos casos NO
            hay calendario a la vista y no se ha escrito nada. El de rango trae el
            literal del servidor; el de conexión ofrece volver a intentarlo. */}
        {!simulando && errorDeSimulacion && (
          <div className="aviso e" role="status" data-prueba="adelanto-sin-vista-previa">
            <b className="tit">
              {errorDeSimulacion?.codigo === 'PARAMETRO_INVALIDO'
                ? 'Esa cifra no se puede usar'
                : 'No pudimos rehacer el calendario'}
            </b>
            {errorDeSimulacion?.codigo === 'PARAMETRO_INVALIDO' ? (
              <>
                {errorDeSimulacion.mensaje}
                {' '}
                Te lo decimos antes de guardar, y no se ha escrito nada: ni el abono, ni el
                calendario. Corrige la cifra de arriba.
              </>
            ) : (
              <>
                Tu cifra está escrita y es la que se va a guardar, pero ahora mismo no podemos
                enseñarte cómo queda tu plan. No te enseñamos el calendario anterior: correspondía a
                otra cifra y sería engañoso.
                {' '}
                <button
                  className="btn sec"
                  type="button"
                  onClick={reSimularConLaCifraEscrita}
                  data-prueba="adelanto-reintentar"
                >
                  Volver a intentarlo
                </button>
              </>
            )}
          </div>
        )}

        {!simulando && !errorDeSimulacion && simulacion && (
          <>
            <div className="fila">
              <span className="crece">Tu deuda pasa de</span>
              <b>{formatearImporte(simulacion.deudaVigente, codigoISO)}</b>
            </div>
            <div className="fila">
              <span className="crece">a</span>
              <b data-prueba="adelanto-deuda-nueva">{formatearImporte(simulacion.deudaNueva, codigoISO)}</b>
            </div>
            <div className="fila">
              <span className="crece">Pagos que te quedan</span>
              <b data-prueba="adelanto-pagos-que-quedan">{simulacion.pagosQueQuedan}</b>
            </div>
            {simulacion.mensualidadesQueSeQuitan > 0 && (
              <div className="fila">
                <span className="crece">Pagos que ya no se cobran</span>
                <b data-prueba="adelanto-quitadas">{simulacion.mensualidadesQueSeQuitan}</b>
              </div>
            )}
            {simulacion.fechaDelUltimoPago && (
              <div className="fila">
                <span className="crece">El último</span>
                <b>{simulacion.fechaDelUltimoPago}</b>
              </div>
            )}
            <div className="fila">
              <span className="crece">Lo que te falta por entregar</span>
              <b>{formatearImporte(simulacion.totalPorEntregar, codigoISO)}</b>
            </div>

            {/* RN-776 · LA MISMA PIEZA del calendario del préstamo que ya usa el
                detalle: ni se copia, ni se reimplementa el pliegue. El estado
                LIQUIDADA de las que el abono quita lo trae ya resuelto el servidor
                (§9), así que aquí no se compara ni una fecha. */}
            <CalendarioDelPrestamo
              mensualidades={simulacion.calendarioResultante.map((m) => ({
                idMensualidad: m.numeroDeMensualidad,
                numeroDeMensualidad: m.numeroDeMensualidad,
                contadorDeMensualidad: m.contadorDeMensualidad,
                importe: m.importe,
                fechaDePago: m.fechaDePago,
                estadoPresentado: m.estadoPresentado,
                esLaQueSigue: false,
              }))}
              codigoISO={codigoISO}
              datoDePrueba="adelanto-calendario"
            />
          </>
        )}
      </div>

      {/* ===== CONFIRMAR, DENTRO DE ESTA MISMA PANTALLA (RN-784f) =========== */}
      {confirmando ? (
        <div className="card" style={{ margin: 12 }} data-prueba="adelanto-confirmacion">
          <h3>¿Registramos este abono?</h3>
          <p>{simulacion?.textoDelEfectoDeRegistrar}</p>
          <button
            className="btn ancho"
            type="button"
            onClick={guardar}
            disabled={guardando}
            data-prueba="adelanto-confirmar"
          >
            {guardando ? 'Registrando…' : 'Sí, registrar el abono'}
          </button>
          <button
            className="btn sec ancho"
            type="button"
            onClick={() => setConfirmando(false)}
            disabled={guardando}
            data-prueba="adelanto-volver-a-revisar"
          >
            Volver a revisar
          </button>
        </div>
      ) : (
        <>
          <button
            className="btn ancho"
            type="button"
            onClick={() => setConfirmando(true)}
            disabled={!puedeGuardar}
            data-prueba="adelanto-registrar"
          >
            Registrar el abono
          </button>
          <button
            className="btn sec ancho"
            type="button"
            onClick={() => navegar(`/cuentas/prestamos/${idCuenta}`)}
            data-prueba="adelanto-cancelar"
          >
            Cancelar
          </button>
        </>
      )}
    </Shell>
  );
}
